import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, LogOut, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { rp, fmtDateTime } from "@/lib/format";
import { useSeo } from "@/lib/shop";
import { StBadge, NEXT_STEP, INSPECT, custPhoto, condLabel, stLabel } from "@/lib/collection";

const KEY = "bk_mk_track";
const field = "w-full h-11 rounded-lg border border-neutral-300 px-3 text-sm bg-white focus:border-neutral-900 outline-none";

function Offers({ it }) {
  if (!it.offers?.length) return null;
  return (
    <div className="mt-3">
      <div className="text-xs font-bold text-neutral-500 mb-1">Riwayat negosiasi</div>
      <ul className="space-y-1 text-sm" data-testid={`offers-${it.id}`}>
        {it.offers.map((o) => (
          <li key={o.id} className="flex flex-wrap justify-between gap-2 rounded-lg bg-neutral-50 border px-3 py-2">
            <span><b>{o.by === "admin" ? (o.final ? "Harga akhir BengkelHobby" : "BengkelHobby") : "Kamu"}</b>: {rp(o.price)}</span>
            <span className="text-xs text-neutral-500">{fmtDateTime(o.at)} · {o.status}{o.expires_at && o.status === "pending" ? ` · berlaku s/d ${fmtDateTime(o.expires_at)}` : ""}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Actions({ sub, it, t, onUpdate }) {
  const [price, setPrice] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (action, extra = {}) => {
    if (["reject_offer", "reject_final", "request_return"].includes(action) && !window.confirm("Yakin melanjutkan aksi ini?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/collection/track/action", { t, item_id: it.id, action, note, ...extra });
      onUpdate(data);
      setPrice(""); setNote("");
      toast.success("Tercatat");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const s = it.status;
  const adminPending = (it.offers || []).some((o) => o.by === "admin" && o.status === "pending");
  const left = sub.max_negotiations + 1 - (it.customer_offers_used || 0);
  const b = "h-10 px-4 rounded-lg text-sm font-bold border-2 border-neutral-900 disabled:opacity-50";
  if (sub.type === "consignment") {
    return (
      <div className="mt-3 space-y-2">
        {s === "approved" && (
          <div className="rounded-xl bg-amber-50 border border-amber-300 p-3 text-sm">
            Komisi BengkelHobby: <b>{it.commission_pct}%</b> dari harga terjual. Harga jual {rp(it.ask_price)}, minimum {rp(it.min_price)}. Masa titip {sub.consign_days} hari sejak barang aktif.
            <button disabled={busy} onClick={() => act("accept_terms")} className={`${b} bg-[var(--bk-yellow)] mt-2 w-full`} data-testid={`act-accept-terms-${it.id}`}>Saya Setuju dengan Persyaratan</button>
          </div>
        )}
        {["active", "expired", "approved", "awaiting_item"].includes(s) && (
          <div className="flex flex-wrap gap-2">
            <input className={`${field} flex-1 min-w-[180px]`} placeholder="Catatan (opsional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`act-note-${it.id}`} />
            {["active", "expired"].includes(s) && <button disabled={busy} onClick={() => act("request_extension")} className={`${b} bg-white`} data-testid={`act-extend-${it.id}`}>Minta Perpanjangan</button>}
            <button disabled={busy} onClick={() => act("request_return")} className={`${b} bg-white`} data-testid={`act-return-${it.id}`}>Ajukan Pengambilan Barang</button>
          </div>
        )}
      </div>
    );
  }
  if (!adminPending) return null;
  if (s === "final_offer_sent") {
    return (
      <div className="mt-3 flex flex-wrap gap-2">
        <button disabled={busy} onClick={() => act("accept_final")} className={`${b} bg-emerald-500 text-white`} data-testid={`act-accept-final-${it.id}`}>Setujui Harga Akhir</button>
        <button disabled={busy} onClick={() => act("reject_final")} className={`${b} bg-white`} data-testid={`act-reject-final-${it.id}`}>Tolak</button>
      </div>
    );
  }
  return (
    <div className="mt-3 space-y-2">
      <div className="flex flex-wrap gap-2">
        <button disabled={busy} onClick={() => act("accept_offer")} className={`${b} bg-emerald-500 text-white`} data-testid={`act-accept-offer-${it.id}`}>Terima Penawaran</button>
        <button disabled={busy} onClick={() => act("reject_offer")} className={`${b} bg-white`} data-testid={`act-reject-offer-${it.id}`}>Tolak</button>
      </div>
      {left > 0 ? (
        <div className="flex flex-wrap gap-2">
          <input type="number" min={1000} className={`${field} w-44`} placeholder="Harga baru (Rp)" value={price} onChange={(e) => setPrice(e.target.value)} data-testid={`act-counter-price-${it.id}`} />
          <input className={`${field} flex-1 min-w-[160px]`} placeholder="Catatan" value={note} onChange={(e) => setNote(e.target.value)} />
          <button disabled={busy || !price} onClick={() => act("counter", { price: Number(price) })} className={`${b} bg-[var(--bk-yellow)]`} data-testid={`act-counter-${it.id}`}>Ajukan Harga ({left}x lagi)</button>
        </div>
      ) : <p className="text-xs text-neutral-500">Batas negosiasi sudah tercapai.</p>}
    </div>
  );
}

function Item({ sub, it, t, onUpdate }) {
  const insp = it.inspection;
  return (
    <div className="rounded-2xl border-2 border-neutral-900 bg-white p-4 sm:p-5" data-testid={`track-item-${it.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div><div className="font-mono text-xs text-neutral-500">{it.id}</div><b className="font-heading text-lg">{it.name}</b><div className="text-sm text-neutral-600">{[it.brand, it.category_name, condLabel(it.condition)].filter(Boolean).join(" · ")}</div></div>
        <StBadge type={sub.type} s={it.status} />
      </div>
      <p className="mt-2 text-sm rounded-lg bg-sky-50 border border-sky-200 px-3 py-2" data-testid={`next-step-${it.id}`}>{NEXT_STEP[sub.type][it.status]}</p>
      <div className="mt-3 flex gap-2 overflow-x-auto">{(it.photos || []).map((p) => <img key={p} src={custPhoto(p, t)} alt="" loading="lazy" className="h-20 w-20 rounded-lg object-cover border shrink-0" />)}</div>
      <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
        {sub.type === "consignment" ? (
          <>
            <div><dt className="text-xs text-neutral-500">Harga jual</dt><dd className="font-bold">{rp(it.ask_price)}</dd></div>
            <div><dt className="text-xs text-neutral-500">Harga minimum</dt><dd className="font-bold">{rp(it.min_price)}</dd></div>
            {it.commission_pct != null && <div><dt className="text-xs text-neutral-500">Komisi</dt><dd className="font-bold">{it.commission_pct}%</dd></div>}
            {it.contract && <div><dt className="text-xs text-neutral-500">Masa titip s/d</dt><dd className="font-bold">{fmtDateTime(it.contract.end_at)}</dd></div>}
            {it.final_price != null && <div><dt className="text-xs text-neutral-500">Terjual</dt><dd className="font-bold">{rp(it.final_price)}</dd></div>}
            {it.payout_amount != null && <div><dt className="text-xs text-neutral-500">Hak kamu</dt><dd className="font-bold text-emerald-700" data-testid={`payout-${it.id}`}>{rp(it.payout_amount)}</dd></div>}
          </>
        ) : (
          <>
            <div><dt className="text-xs text-neutral-500">Harga awal kamu</dt><dd className="font-bold">{rp(it.offer_price)}</dd></div>
            {it.final_price != null && <div><dt className="text-xs text-neutral-500">Harga akhir</dt><dd className="font-bold">{rp(it.final_price)}</dd></div>}
          </>
        )}
        <div><dt className="text-xs text-neutral-500">Pembayaran</dt><dd className="font-bold">{it.payout ? `Dibayar ${fmtDateTime(it.payout.paid_at)}` : it.purchase ? `Dibayar ${fmtDateTime(it.purchase.paid_at)}` : "Belum"}</dd></div>
      </dl>
      {insp && (
        <div className="mt-3 rounded-xl border p-3 text-sm" data-testid={`inspection-${it.id}`}>
          <b>Hasil inspeksi: {INSPECT[insp.result]}</b> <span className="text-xs text-neutral-500">{fmtDateTime(insp.at)}</span>
          {insp.physical && <p>Kondisi fisik: {insp.physical}</p>}{insp.completeness && <p>Kelengkapan: {insp.completeness}</p>}{insp.damage_notes && <p>Catatan: {insp.damage_notes}</p>}
          <div className="mt-2 flex gap-2 overflow-x-auto">{(insp.photos || []).map((p) => <img key={p} src={custPhoto(p, t)} alt="" className="h-16 w-16 rounded object-cover border" />)}</div>
        </div>
      )}
      <Offers it={it} />
      <Actions sub={sub} it={it} t={t} onUpdate={onUpdate} />
    </div>
  );
}

export default function CollectionTrack() {
  useSeo("Lacak Pengajuan Koleksi", "Lacak status titip jual atau penjualan koleksi kamu di BengkelHobby.");
  const [sp] = useSearchParams();
  const [t, setT] = useState(sp.get("t") || sessionStorage.getItem(KEY) || "");
  const [form, setForm] = useState({ sub_no: sp.get("s") || "", pin: "" });
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!t) return;
    api.get("/collection/track", { params: { t } }).then((r) => { setSub(r.data); sessionStorage.setItem(KEY, t); }).catch((e) => { toast.error(errMsg(e)); sessionStorage.removeItem(KEY); setT(""); });
  }, [t]);
  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/collection/track/login", form);
      setT(data.token);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const logout = () => { sessionStorage.removeItem(KEY); setT(""); setSub(null); };
  if (!sub) {
    return (
      <div className="container-x py-12 max-w-md" data-testid="mk-track-login">
        <h1 className="font-heading text-3xl font-extrabold">Lacak Pengajuan Koleksi</h1>
        <p className="text-sm text-neutral-600 mt-1">Masukkan Submission ID dan Access PIN yang kamu terima saat mengajukan.</p>
        {t ? <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div> : (
          <form onSubmit={login} className="mt-6 space-y-3 rounded-2xl border-2 border-neutral-900 bg-white p-5">
            <input className={field} placeholder="CON-202611-0001 / BUY-202611-0001" value={form.sub_no} onChange={(e) => setForm({ ...form, sub_no: e.target.value.toUpperCase() })} required data-testid="mk-track-id" />
            <input className={`${field} font-mono tracking-widest`} placeholder="Access PIN" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.toUpperCase() })} required data-testid="mk-track-pin" />
            <button className="btn-ink w-full" disabled={busy} data-testid="mk-track-submit"><Search className="h-4 w-4" />Lacak</button>
          </form>
        )}
      </div>
    );
  }
  return (
    <div className="container-x py-10 max-w-4xl" data-testid="mk-track-view">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-bold text-neutral-500">{sub.type === "consignment" ? "CONSIGNMENT — TITIP JUAL" : "SELL TO US — JUAL LANGSUNG"}</div>
          <h1 className="font-mono text-2xl sm:text-3xl font-bold" data-testid="mk-track-subno">{sub.sub_no}</h1>
          <p className="text-sm text-neutral-600">Diajukan {fmtDateTime(sub.created_at)} · {sub.customer.name} · {sub.items.length} item · Status: {stLabel(sub.type, sub.summary_status)}</p>
        </div>
        <button onClick={logout} className="btn-brand bg-white" data-testid="mk-track-logout"><LogOut className="h-4 w-4" />Keluar</button>
      </div>
      {sub.receiving && (
        <div className="mt-5 rounded-xl bg-sky-50 border border-sky-200 p-4 text-sm space-y-1" data-testid="mk-track-receiving">
          <b>Kirim barang ke:</b> {sub.receiving.receiving_name} — {sub.receiving.receiving_address} ({sub.receiving.receiving_phone})
          <p>Tulis nomor pengajuan <b>{sub.sub_no}</b> di paket. {sub.receiving.packing_instructions}</p>
          <p>{sub.receiving.shipping_info} {sub.receiving.shipping_cost_terms}</p>
        </div>
      )}
      <div className="mt-6 space-y-5">{sub.items.map((it) => <Item key={it.id} sub={sub} it={it} t={t} onUpdate={setSub} />)}</div>
      <section className="mt-8">
        <h2 className="font-heading text-xl font-bold mb-3">Riwayat Aktivitas</h2>
        <ol className="space-y-2 text-sm" data-testid="mk-track-logs">
          {[...sub.logs].reverse().map((l, i) => (
            <li key={i} className="rounded-lg border bg-white px-3 py-2"><span className="text-xs text-neutral-500">{fmtDateTime(l.at)}{l.item_id ? ` · ${l.item_id}` : ""}</span><div>{l.note || stLabel(sub.type, l.status)}</div></li>
          ))}
        </ol>
      </section>
    </div>
  );
}
